import { useEffect, useState } from "react";
import type { SelectProps } from "@amzn/awsui-components-react/polaris/select";
import {
  useLazySearchUsersQuery,
  type UserOutput,
} from "components/user-search-input/endpoints";
import { runInBatches } from "utils/async-utils";
import { GaiaConstants } from "utils/gaia-constants";

// Cap on concurrent directory lookups so a large list doesn't fan out into
// hundreds of simultaneous requests. The directory has no batch endpoint, so
// aliases are resolved in capped chunks.
const RESOLVE_BATCH_SIZE = 10;

export interface UseEnrichedMemberOptions {
  options: SelectProps.Option[];
  isEnriching: boolean;
}

// Enriches a static team-member option list with directory (PAPI) display info:
// each option keeps its value/label (the alias) and gains a "Full Name · Title"
// description. It never adds, removes, or reorders options — it only attaches
// display metadata — so any caller-applied filtering (e.g. the reviews
// "can't pick yourself" rule) is preserved. Options with no resolvable alias,
// or already carrying a description, pass through unchanged.
//
// `enabled` (default true) lets a caller defer the lookups — e.g. a single-
// select owner picker can pass `enabled` only once its dropdown is opened, so
// the full-roster directory fan-out never runs on mount for a form the user
// may never interact with. While disabled the options pass through unenriched
// (bare aliases), which is also the graceful-degradation path when PAPI fails.
//
// Lookups are also skipped when there is no real team — a falsy teamName or the
// GaiaConstants.PLACE_HOLDER sentinel (truthy, but not a selectable team).
//
// Only use this for the static team-member list. Selections driven by a live
// PAPI search window (e.g. UserSearchInput) should not use this.
export const useEnrichedMemberOptions = (
  baseOptions: SelectProps.Option[],
  teamName: string | undefined,
  enabled = true,
): UseEnrichedMemberOptions => {
  const [searchUsers] = useLazySearchUsersQuery();
  const [descriptions, setDescriptions] = useState<Record<string, string>>({});
  const [isEnriching, setIsEnriching] = useState(false);

  // Aliases needing a lookup: those with a value and no existing description.
  const aliasKey = baseOptions
    .filter((option) => option.value && !option.description)
    .map((option) => option.value)
    .join(",");

  useEffect(() => {
    const aliases = aliasKey ? aliasKey.split(",") : [];
    // Skip when disabled, when there is no real team (the PLACE_HOLDER sentinel
    // is truthy but not a selectable team, so it must not trigger lookups), or
    // when there is nothing to resolve.
    if (
      !enabled ||
      !teamName ||
      teamName === GaiaConstants.PLACE_HOLDER ||
      aliases.length === 0
    ) {
      setIsEnriching(false);
      return;
    }

    let cancelled = false;
    setIsEnriching(true);

    const resolve = async (alias: string): Promise<[string, string]> => {
      const { employees } = await searchUsers({
        teamName,
        keyword: alias,
      }).unwrap();
      const match = employees.find(
        (employee: UserOutput) => employee.alias === alias,
      );
      const label =
        match?.fullName && match?.businessTitle
          ? `${match.fullName} · ${match.businessTitle}`
          : (match?.fullName ?? "");
      return [alias, label];
    };

    runInBatches(aliases, RESOLVE_BATCH_SIZE, resolve).then((settled) => {
      if (cancelled) {
        return;
      }
      const next: Record<string, string> = {};
      for (const result of settled) {
        if (result.status === "fulfilled") {
          const [alias, label] = result.value;
          if (label) {
            next[alias] = label;
          }
        }
      }
      setDescriptions(next);
      setIsEnriching(false);
    });

    return () => {
      cancelled = true;
    };
  }, [enabled, teamName, aliasKey, searchUsers]);

  const options = baseOptions.map((option) => {
    const resolved = option.value ? descriptions[option.value] : undefined;
    return option.description || !resolved
      ? option
      : { ...option, description: resolved };
  });

  return { options, isEnriching };
};
